import { useEffect, useState } from 'react';
import type {
  BloqueioMotivo,
  BloquearEntregadoresResult,
} from '../../../../api/services/romaneioService';
import { Button } from '../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Label } from '../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Textarea } from '../../../../components/ui/textarea';
import { formatDocumento, parseDocumentos } from './bloqueio-utils';
import { mensagemErro, useBloquearEntregadores } from './use-bloqueios';

interface BloquearDialogProps {
  acao: 'bloquear' | 'desbloquear' | null;
  motivos: BloqueioMotivo[];
  onClose: () => void;
}

// Bloqueio/desbloqueio de um ou vários CPFs/CNPJs na ops atual: lista colada ou CSV.
export function BloquearDialog({
  acao,
  motivos,
  onClose,
}: BloquearDialogProps) {
  const mutation = useBloquearEntregadores();
  const [texto, setTexto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [motivoTexto, setMotivoTexto] = useState('');
  const [resultado, setResultado] = useState<BloquearEntregadoresResult | null>(
    null
  );
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setTexto('');
    setMotivo('');
    setMotivoTexto('');
    setResultado(null);
    setErro(null);
  }, [acao]);

  if (!acao) return null;

  const bloquear = acao === 'bloquear';
  const documentos = parseDocumentos(texto);
  const outro = motivo === 'outro';
  // Bloqueio sempre com motivo; "outro" exige a descrição.
  const podeEnviar =
    documentos.length > 0 &&
    (!bloquear || (!!motivo && (!outro || !!motivoTexto.trim()))) &&
    !mutation.isPending;

  const lerArquivo = async (file?: File) => {
    if (!file) return;
    const conteudo = await file.text();
    setTexto((atual) => (atual ? `${atual}\n${conteudo}` : conteudo));
  };

  const enviar = async () => {
    setErro(null);
    try {
      const res = await mutation.mutateAsync({
        documentos,
        acao,
        motivo_codigo: bloquear ? motivo : undefined,
        motivo_texto: bloquear && outro ? motivoTexto.trim() : undefined,
      });
      setResultado(res);
    } catch (e) {
      setErro(mensagemErro(e));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {bloquear ? 'Bloquear entregadores' : 'Desbloquear entregadores'}
          </DialogTitle>
          <DialogDescription>
            {bloquear
              ? 'Os entregadores bloqueados deixam de receber ofertas de antecipação nesta operação.'
              : 'Só é possível desbloquear o que foi bloqueado pela sua operação. Bloqueios feitos pelo gestor precisam ser tratados com o gestor.'}
          </DialogDescription>
        </DialogHeader>

        {resultado ? (
          <Resultado resultado={resultado} bloquear={bloquear} />
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bloqueio-docs">CPFs/CNPJs</Label>
              <Textarea
                id="bloqueio-docs"
                rows={6}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder={
                  'Um por linha, ou separados por vírgula\n123.456.789-01\n12.345.678/0001-95'
                }
              />
              <div className="flex items-center justify-between text-sm text-[hsl(215,16%,47%)]">
                <span>{documentos.length} documento(s)</span>
                <input
                  type="file"
                  accept=".csv,.txt"
                  aria-label="Carregar CSV"
                  onChange={(e) => lerArquivo(e.target.files?.[0])}
                />
              </div>
            </div>

            {bloquear && (
              <div className="space-y-2">
                <Label>Motivo</Label>
                <Select value={motivo} onValueChange={setMotivo}>
                  <SelectTrigger aria-label="Motivo">
                    <SelectValue placeholder="Selecione o motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    {motivos.map((m) => (
                      <SelectItem key={m.codigo} value={m.codigo}>
                        {m.rotulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {outro && (
                  <Textarea
                    aria-label="Descreva o motivo"
                    rows={2}
                    value={motivoTexto}
                    onChange={(e) => setMotivoTexto(e.target.value)}
                    placeholder="Descreva o motivo"
                  />
                )}
              </div>
            )}

            {erro && <p className="text-sm text-red-600">{erro}</p>}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            {resultado ? 'Fechar' : 'Cancelar'}
          </Button>
          {!resultado && (
            <Button
              onClick={enviar}
              disabled={!podeEnviar}
              variant={bloquear ? 'destructive' : 'default'}
            >
              {mutation.isPending
                ? 'Enviando...'
                : bloquear
                  ? 'Confirmar bloqueio'
                  : 'Confirmar desbloqueio'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Resultado({
  resultado,
  bloquear,
}: {
  resultado: BloquearEntregadoresResult;
  bloquear: boolean;
}) {
  return (
    <div className="space-y-3 text-sm">
      <p className="font-medium text-green-700">
        {resultado.success.length}{' '}
        {bloquear ? 'bloqueado(s)' : 'desbloqueado(s)'}
      </p>
      {resultado.failed.length > 0 && (
        <Lista
          titulo={`${resultado.failed.length} não processado(s)`}
          cor="text-red-700"
          itens={resultado.failed}
        />
      )}
      {resultado.avisos.length > 0 && (
        <Lista
          titulo={`${resultado.avisos.length} aviso(s)`}
          cor="text-amber-700"
          itens={resultado.avisos}
        />
      )}
    </div>
  );
}

function Lista({
  titulo,
  cor,
  itens,
}: {
  titulo: string;
  cor: string;
  itens: { documento: string; reason: string }[];
}) {
  return (
    <div>
      <p className={`font-medium ${cor}`}>{titulo}</p>
      <ul className="max-h-40 overflow-y-auto text-[hsl(215,16%,47%)]">
        {itens.map((i, idx) => (
          <li key={idx}>
            {formatDocumento(i.documento)}: {i.reason}
          </li>
        ))}
      </ul>
    </div>
  );
}
